import { loadModal } from '../AsyncLoader';
import FormHandler from '../forms/FormHandler';
import FormSubmitButton from '../forms/FormSubmitButton';
import Messenger from '../tnew/ui/Messenger';
import dom from '../utils/dom';
import ApplyPromoCodeFailureReason from './ApplyPromoCodeFailureReason';
import ApplyPromoCodeFormFields from './ApplyPromoCodeFormFields';
import IPromoCodeProviderElements from './IPromoCodeProviderElements';
import PromoCodeService from './PromoCodeService';
import UtilityNavigationComponent from './UtilityNavigationComponent';

function reloadFromSuccessfulPromo() {
  const url = new URL(document.location.href);

  url.searchParams.set('promoapplied', 'true');

  window.location.href = url.toString();
}

function initPromoCodeRemoval($btnRemove: HTMLButtonElement, $label: HTMLSpanElement) {
  let isRemoving = false;

  $btnRemove.addEventListener('click', async () => {
    if (isRemoving) return;

    const messenger = getStandardMessenger();

    messenger.clearMessages();

    isRemoving = true;

    dom.hide($label);
    FormSubmitButton.applyBusyStyling($btnRemove, { disableElement: false });

    const response = await PromoCodeService.remove();

    if (response.isSuccessful) {
        const url = new URL(document.location.href);

        if (url.searchParams.has('promoapplied')) {
            url.searchParams.delete('promoapplied');
        }

        window.location.href = url.toString();

        return;
    }

    isRemoving = false;

    dom.unhide($label);
    FormSubmitButton.resetButtonStyling($btnRemove);

    messenger.addErrorMessage(response.errorMessage, true);
  });
}

function initApplyPromoCodeMobile(
  $mobileForm: HTMLFormElement,
  $btnShowModal: HTMLButtonElement,
) {
  dom.remove($mobileForm);
  dom.unhide($mobileForm);

  const formHandler = new FormHandler($mobileForm, {
    onValidationSuccess: async ({ stopSubmitting }) => {
      const controlGroup = formHandler.getControlGroup(ApplyPromoCodeFormFields.PromoCode);

      const promoCode = controlGroup?.input.value || '';

      const response = await PromoCodeService.apply(promoCode);

      if (response.isSuccessful) {
        reloadFromSuccessfulPromo();
        return;
      }

      stopSubmitting();

      if (shouldShowMobileErrorMessage(response.reason)) {
        formHandler.processValidationResults([
          { fieldId: ApplyPromoCodeFormFields.PromoCode, isValid: false, message: response.errorMessage },
        ]);
      }
    },
  });

  $btnShowModal.addEventListener('click', async () => {
    const Modal = await loadModal();

    const $btnSubmit = Modal.utils.makePrimaryButton('Submit')[0];
    $btnSubmit.setAttribute('type', 'submit');
    $btnSubmit.setAttribute('form', $mobileForm.getAttribute('id')!);

    const $btnCancel = Modal.utils.makeSecondaryButton('Cancel')[0];

    const modal = Modal.open({
      title: UtilityNavigationComponent.promoCodeEntrySubhead,
      bodyContents: $mobileForm,
      footerContents: [$btnSubmit, $btnCancel],
    });

    $btnCancel.addEventListener('click', () => {
      modal.close();
    });
  });
}

function shouldShowMobileErrorMessage(reason: string) {
  switch (reason) {
    case ApplyPromoCodeFailureReason.InvalidPromoCode:
    case ApplyPromoCodeFailureReason.MosConflict:
      return true;
    default:
      return false;
  }
}

function initApplyPromoCodeDesktop(
  $helpBlockContainer: HTMLDivElement,
  $input: HTMLInputElement,
  $form: HTMLFormElement,
  $label: HTMLSpanElement,
  $btn: HTMLButtonElement,
  $helpBlockText: HTMLDivElement,
  $helpBlock: HTMLDivElement,
) {
  const hideHelpBlock = () => {
    dom.hide($helpBlockContainer);

    $helpBlockContainer.style.height = '0px';
  };

  let isSubmitting = false;

  const messenger = getStandardMessenger();

  $form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    isSubmitting = true;

    messenger.clearMessages();

    hideHelpBlock();

    $label.classList.add('sr-only');
    FormSubmitButton.applyBusyStyling($btn, { disableElement: false });
    $input.removeAttribute('aria-describedby');
    $input.removeAttribute('aria-invalid');

    const stopSubmitting = () => {
      $label.classList.remove('sr-only');

      FormSubmitButton.resetButtonStyling($btn);

      isSubmitting = false;
    };

    const response = await PromoCodeService.apply($input.value);

    if (response.isSuccessful) {
      reloadFromSuccessfulPromo();
      return;
    }

    stopSubmitting();

    if (response.reason === ApplyPromoCodeFailureReason.InvalidPromoCode) {
      $helpBlockText.innerText = response.errorMessage;
      $input.setAttribute('aria-describedby', $helpBlock.id);
      $input.setAttribute('aria-invalid', 'true');

      dom.unhide($helpBlockContainer);

      const { height } = window.getComputedStyle($helpBlock);

      $helpBlockContainer.style.height = height;
    }

    if (response.reason === ApplyPromoCodeFailureReason.MosConflict) {
      messenger.addErrorMessage(response.errorMessage, true);
    }

    if (response.reason === ApplyPromoCodeFailureReason.PromoCartItemsConflict) {
      messenger.addErrorMessage(response.errorMessage, true);
    }
    // Promo code error state - place focus back on input
    $input.focus();
  });
}

function init(elements: IPromoCodeProviderElements) {
  const els = elements.getAll();

  if (els.$btnRemove) {
    initPromoCodeRemoval(els.$btnRemove, els.$label);
    return;
  }

  if (!els.$form) {
    return;
  }

  initApplyPromoCodeMobile(els.$mobileForm, els.$btnShowModal);

  initApplyPromoCodeDesktop(els.$helpBlockContainer, els.$input, els.$form, els.$btnApplySubmitLabel, els.$btn, els.$helpBlockText, els.$helpBlock);
}

function getStandardMessenger() {
  return new Messenger(document.querySelector('#tn-error-message-template') as HTMLElement);
}

export default {
  init,
};
